import { lazy, Suspense } from 'react';
import type {
  LatLng,
  Listing,
  MapBounds,
  NeighborhoodFeature,
  OfficialCell,
  OfficialPin,
} from '../../domain/types';
import { appConfig } from '../../lib/config';
import { DemoMap } from './DemoMap';

// Preload at module evaluation: the map is the page's main content, and
// waiting for React to mount MapStage before fetching the chunk added a
// full network hop to the startup cascade.
const realMapPromise = appConfig.googleMapsApiKey ? import('./RealMap') : null;
const RealMap = lazy(() => realMapPromise ?? import('./RealMap'));

/** Explicit camera move (search, GPS, shared link). The real map is
 * uncontrolled — center/zoom state echoes would fight the user's gesture —
 * so programmatic moves travel through this command channel instead. */
export type CameraCommand = { center: LatLng; zoom: number; id: number };

export type MapStageProps = {
  center: LatLng;
  zoom: number;
  bounds: MapBounds;
  cameraCommand: CameraCommand | null;
  listings: Listing[];
  /** Official registry bubbles (aggregated cells), filtered by source mode. */
  officialCells: OfficialCell[];
  /** Official registry pins (street zoom), already filtered by source mode. */
  officialPins: OfficialPin[];
  selectedId: string | null;
  // Only for the demo map's watermark label; polygons are no longer drawn
  // (the bundled boundaries are placeholder rectangles, not real limits).
  activeNeighborhood: NeighborhoodFeature | null;
  placementMode: boolean;
  placementPosition: LatLng | null;
  onViewportChange: (center: LatLng, zoom: number, bounds: MapBounds) => void;
  onSelectListing: (listing: Listing) => void;
  onSelectOfficial: (pin: OfficialPin) => void;
  onSelectOfficialStack: (pins: OfficialPin[]) => void;
  onPickLocation: (position: LatLng) => void;
};

export function MapStage(props: MapStageProps) {
  if (!appConfig.googleMapsApiKey) return <DemoMap {...props} />;
  return (
    <Suspense
      fallback={
        <div className="map-loading" role="status">
          <span />
          Cargando el mapa…
        </div>
      }
    >
      <RealMap {...props} apiKey={appConfig.googleMapsApiKey} mapId={appConfig.googleMapsMapId} />
    </Suspense>
  );
}
